import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Cpu,
  AlertTriangle,
  Wrench,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  Calendar,
  FileText,
  ShieldAlert,
  Clock,
  Sparkles,
  TrendingUp,
  BookOpen,
} from "lucide-react";
import { dashboardAPI } from "../api";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import clsx from "clsx";

const StatCard = ({
  title,
  value,
  change,
  changeType,
  icon: Icon,
  color = "blue",
  subtitle,
}) => {
  const colorThemes = {
    blue: {
      bg: "from-blue-50/90 to-white border-blue-100/80 hover:border-blue-200",
      icon: "bg-blue-100/80 text-blue-600",
      badge: "bg-blue-100 text-blue-700",
    },
    green: {
      bg: "from-emerald-50/90 to-white border-emerald-100/80 hover:border-emerald-200",
      icon: "bg-emerald-100/80 text-emerald-600",
      badge: "bg-emerald-100 text-emerald-700",
    },
    yellow: {
      bg: "from-amber-50/90 to-white border-amber-100/80 hover:border-amber-200",
      icon: "bg-amber-100/80 text-amber-600",
      badge: "bg-amber-100 text-amber-700",
    },
    red: {
      bg: "from-rose-50/90 to-white border-rose-100/80 hover:border-rose-200",
      icon: "bg-rose-100/80 text-rose-600",
      badge: "bg-rose-100 text-rose-700",
    },
    purple: {
      bg: "from-indigo-50/90 to-white border-indigo-100/80 hover:border-indigo-200",
      icon: "bg-indigo-100/80 text-indigo-600",
      badge: "bg-indigo-100 text-indigo-700",
    },
  };

  const theme = colorThemes[color] || colorThemes.blue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={clsx(
        "p-4 sm:p-5 bg-gradient-to-br border rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between relative overflow-hidden group",
        theme.bg
      )}
    >
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className={clsx("p-2.5 rounded-xl flex-shrink-0", theme.icon)}>
            <Icon className="w-5 h-5" />
          </div>
          {change && (
            <span
              className={clsx(
                "inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full truncate",
                changeType === "increase"
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-rose-100 text-rose-700"
              )}
            >
              {changeType === "increase" ? (
                <ArrowUpRight className="w-3.5 h-3.5" />
              ) : (
                <ArrowDownRight className="w-3.5 h-3.5" />
              )}
              {change}
            </span>
          )}
        </div>

        <div>
          <p className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-0.5">
            {value}
          </p>
          <p className="text-xs sm:text-sm text-slate-600 font-medium truncate">
            {title}
          </p>
        </div>
      </div>

      <div className="mt-3 pt-2 border-t border-slate-100/80 flex items-center justify-between text-xs">
        {subtitle ? (
          <span className="text-slate-500 font-medium truncate">{subtitle}</span>
        ) : (
          <span className="text-slate-400">Live telemetry</span>
        )}
      </div>
    </motion.div>
  );
};

const AlertItem = ({ alert }) => {
  const severityColors = {
    critical: "border-rose-200 bg-rose-50/70 text-rose-900",
    high: "border-amber-200 bg-amber-50/70 text-amber-900",
    medium: "border-blue-200 bg-blue-50/70 text-blue-900",
    low: "border-slate-200 bg-slate-50/70 text-slate-800",
  };

  const severityIcons = {
    critical: <XCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />,
    high: <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />,
    medium: <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />,
    low: <AlertCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />,
  };

  const formattedTime = alert.created_at
    ? new Date(alert.created_at).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })
    : "Recent";

  return (
    <div
      className={clsx(
        "p-3 sm:p-3.5 rounded-xl border transition-all hover:shadow-sm",
        severityColors[alert.severity] || severityColors.low
      )}
    >
      <div className="flex items-start gap-2.5">
        {severityIcons[alert.severity] || severityIcons.low}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-semibold text-xs sm:text-sm truncate">
              {alert.title || "Equipment Anomaly Detected"}
            </p>
            <span className="text-[11px] opacity-70 flex-shrink-0">
              {formattedTime}
            </span>
          </div>
          <p className="text-xs opacity-80 mt-0.5 truncate">
            {alert.machine_name || "General Machine Unit"}
          </p>
        </div>
      </div>
    </div>
  );
};

const DashboardContentSkeleton = ({ sidebarOpen = true }) => (
  <div className="space-y-5 lg:space-y-6">
    {/* 6 Responsive KPI Stat Cards Skeleton */}
    <div
      className={clsx(
        "grid gap-3 sm:gap-4 transition-all duration-300",
        sidebarOpen
          ? "grid-cols-2 sm:grid-cols-3 xl:grid-cols-6"
          : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
      )}
    >
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="bg-white rounded-2xl border border-slate-200/80 p-4 sm:p-5 shadow-sm flex flex-col justify-between relative overflow-hidden h-full min-h-[148px] animate-pulse"
        >
          <div>
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-slate-200 flex-shrink-0" />
              <div className="h-5 w-16 bg-slate-100 rounded-full" />
            </div>
            <div>
              <div className="h-6 sm:h-7 w-20 bg-slate-200 rounded-lg mb-1.5" />
              <div className="h-3.5 sm:h-4 w-24 bg-slate-100 rounded" />
            </div>
          </div>
          <div className="mt-3 pt-2 border-t border-slate-100/80 flex items-center justify-between">
            <div className="h-3 w-24 bg-slate-100 rounded" />
          </div>
        </div>
      ))}
    </div>

    {/* Row 2: Equipment Health Distribution & Maintenance Trend Skeletons */}
    <div
      className={clsx(
        "grid gap-4 sm:gap-6 transition-all duration-300",
        sidebarOpen
          ? "grid-cols-1 lg:grid-cols-3"
          : "grid-cols-1 lg:grid-cols-12"
      )}
    >
      {/* Fleet Health Breakdown Card Skeleton */}
      <div
        className={clsx(
          "bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col justify-between animate-pulse",
          sidebarOpen ? "lg:col-span-1" : "lg:col-span-4"
        )}
      >
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="space-y-1.5">
              <div className="h-5 w-36 bg-slate-200 rounded" />
              <div className="h-3.5 w-28 bg-slate-100 rounded" />
            </div>
            <div className="h-6 w-20 bg-slate-100 rounded-full" />
          </div>
          <div className="w-full h-3 rounded-full bg-slate-100 mb-5" />
          <div className="space-y-2.5">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-12 bg-slate-50/80 border border-slate-100 rounded-xl p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-200" />
                  <div className="h-4 w-20 bg-slate-200 rounded" />
                </div>
                <div className="h-4 w-12 bg-slate-200 rounded" />
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
          <div className="h-4 w-32 bg-slate-200 rounded" />
          <div className="w-4 h-4 rounded bg-slate-200" />
        </div>
      </div>

      {/* Maintenance Activity Trend Skeleton */}
      <div
        className={clsx(
          "bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col justify-between animate-pulse",
          sidebarOpen ? "lg:col-span-2" : "lg:col-span-8"
        )}
      >
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <div className="space-y-1.5">
              <div className="h-5 w-48 bg-slate-200 rounded" />
              <div className="h-3.5 w-36 bg-slate-100 rounded" />
            </div>
            <div className="flex items-center gap-2">
              <div className="h-6 w-24 bg-slate-100 rounded-lg" />
              <div className="h-6 w-24 bg-slate-100 rounded-lg" />
            </div>
          </div>
          <div className="h-56 sm:h-64 w-full flex items-end gap-3 justify-between px-2 pt-4">
            <div className="w-full h-24 bg-slate-200/70 rounded-t-lg" />
            <div className="w-full h-36 bg-slate-200/70 rounded-t-lg" />
            <div className="w-full h-28 bg-slate-200/70 rounded-t-lg" />
            <div className="w-full h-44 bg-slate-200/70 rounded-t-lg" />
            <div className="w-full h-32 bg-slate-200/70 rounded-t-lg" />
            <div className="w-full h-48 bg-slate-200/70 rounded-t-lg" />
            <div className="w-full h-40 bg-slate-200/70 rounded-t-lg" />
          </div>
        </div>
        <div className="mt-2 pt-3 border-t border-slate-100 flex items-center justify-between">
          <div className="h-4 w-40 bg-slate-100 rounded" />
          <div className="h-4 w-32 bg-slate-200 rounded" />
        </div>
      </div>
    </div>

    {/* Row 3: Active Alerts, Recent Activity & Quick Operations Skeletons */}
    <div
      className={clsx(
        "grid gap-4 sm:gap-6 transition-all duration-300",
        sidebarOpen
          ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
          : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
      )}
    >
      {/* Active Alerts Skeleton */}
      <div className="bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col justify-between animate-pulse">
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-lg bg-slate-200" />
              <div className="h-5 w-28 bg-slate-200 rounded" />
            </div>
            <div className="h-5 w-16 bg-slate-100 rounded-full" />
          </div>
          <div className="space-y-2.5">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="p-3 rounded-xl border border-slate-100 bg-slate-50/70 flex items-start gap-2.5"
              >
                <div className="w-4 h-4 rounded-full bg-slate-200 flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="h-4 w-32 bg-slate-200 rounded" />
                    <div className="h-3 w-12 bg-slate-100 rounded" />
                  </div>
                  <div className="h-3 w-20 bg-slate-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
          <div className="h-4 w-36 bg-slate-200 rounded" />
          <div className="w-4 h-4 rounded bg-slate-200" />
        </div>
      </div>

      {/* Recent Activity Skeleton */}
      <div className="bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col justify-between animate-pulse">
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-lg bg-slate-200" />
              <div className="h-5 w-32 bg-slate-200 rounded" />
            </div>
            <div className="h-5 w-24 bg-slate-100 rounded-full" />
          </div>
          <div className="space-y-2.5">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="p-3 rounded-xl border border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3"
              >
                <div className="space-y-1.5 flex-1">
                  <div className="h-4 w-36 bg-slate-200 rounded" />
                  <div className="h-3 w-24 bg-slate-100 rounded" />
                </div>
                <div className="h-5 w-16 bg-slate-100 rounded-md" />
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
          <div className="h-4 w-44 bg-slate-100 rounded" />
          <div className="h-4 w-16 bg-slate-100 rounded" />
        </div>
      </div>

      {/* Quick Actions Skeleton */}
      <div
        className={clsx(
          "bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col justify-between animate-pulse",
          sidebarOpen ? "md:col-span-2 xl:col-span-1" : "md:col-span-2 lg:col-span-1"
        )}
      >
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-lg bg-slate-200" />
              <div className="h-5 w-28 bg-slate-200 rounded" />
            </div>
            <div className="h-5 w-20 bg-slate-100 rounded-full" />
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="p-3 rounded-xl border border-slate-100 bg-slate-50/50 flex flex-col justify-between h-24"
              >
                <div className="w-8 h-8 rounded-lg bg-slate-200 mb-2" />
                <div className="space-y-1">
                  <div className="h-3.5 w-20 bg-slate-200 rounded" />
                  <div className="h-2.5 w-14 bg-slate-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
          <div className="h-3.5 w-36 bg-slate-100 rounded" />
          <div className="h-3.5 w-24 bg-slate-200 rounded" />
        </div>
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const { sidebarOpen = true } = useOutletContext() || {};

  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
  } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => dashboardAPI.getData().then((res) => res.data.data),
  });

  const {
    data: alertsData = [],
  } = useQuery({
    queryKey: ["dashboard-alerts"],
    queryFn: () =>
      dashboardAPI.getAlerts({ limit: 5 }).then((res) => res.data.data || []),
  });

  const showSkeleton = isDashboardLoading;

  const {
    machineStatus = {},
    alerts = {},
    maintenance = {},
    predictiveInsights = {},
    recentActivity = [],
  } = dashboardData || {};

  const totalMachinesCount =
    Number(machineStatus.total) ||
    (Number(machineStatus.operational) || 0) +
    (Number(machineStatus.in_maintenance) || 0) +
    (Number(machineStatus.error) || 0) +
    (Number(machineStatus.offline) || 0) ||
    0;

  const operationalCount = Number(machineStatus.operational) || 0;
  const inMaintenanceCount = Number(machineStatus.in_maintenance) || 0;
  const offlineErrorCount =
    (Number(machineStatus.error) || 0) + (Number(machineStatus.offline) || 0);

  const operationalPercent = totalMachinesCount
    ? Math.round((operationalCount / totalMachinesCount) * 100)
    : 100;
  const inMaintenancePercent = totalMachinesCount
    ? Math.round((inMaintenanceCount / totalMachinesCount) * 100)
    : 0;
  const offlinePercent = totalMachinesCount
    ? Math.max(0, 100 - operationalPercent - inMaintenancePercent)
    : 0;

  const totalActiveAlerts =
    (Number(alerts.critical) || 0) +
    (Number(alerts.high) || 0) +
    (Number(alerts.medium) || 0);

  const pendingInsightCount =
    (Number(predictiveInsights.critical) || 0) +
    (Number(predictiveInsights.high) || 0);

  const pendingMaintenanceTotal =
    (Number(maintenance.upcoming) || 0) + (Number(maintenance.inProgress) || 0);

  const defaultTrendData = [
    { name: "Mon", tasks: 4, compliance: 92 },
    { name: "Tue", tasks: 3, compliance: 95 },
    { name: "Wed", tasks: 6, compliance: 98 },
    { name: "Thu", tasks: 2, compliance: 94 },
    { name: "Fri", tasks: 5, compliance: 91 },
    { name: "Sat", tasks: 2, compliance: 99 },
    { name: "Sun", tasks: 3, compliance: 97 },
  ];

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Top Header with Live Indicator & Action Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Operations Dashboard
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Real-time status of plant equipment, maintenance schedules & active workflows
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">
          <Link
            to="/assistant"
            className="btn btn-primary text-xs sm:text-sm py-2 px-3 sm:px-4 shadow-md shadow-blue-500/20 whitespace-nowrap"
          >
            <Sparkles className="w-4 h-4" /> Ask AI Assistant
          </Link>
        </div>
      </div>

      {showSkeleton ? (
        <DashboardContentSkeleton sidebarOpen={sidebarOpen} />
      ) : (
        <>
          {/* 6 Responsive KPI Stat Cards: Automatically adjusts layout when sidebar collapses */}
          <div
            className={clsx(
              "grid gap-3 sm:gap-4 transition-all duration-300",
              sidebarOpen
                ? "grid-cols-2 sm:grid-cols-3 xl:grid-cols-6"
                : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
            )}
          >
            <StatCard
              title="Fleet Assets"
              value={totalMachinesCount}
              icon={Cpu}
              color="blue"
              change={`${operationalCount} live`}
              changeType="increase"
              subtitle={`${operationalPercent}% operational`}
            />
            <StatCard
              title="Operating Rate"
              value={`${operationalPercent}%`}
              icon={Activity}
              color="green"
              change={operationalPercent >= 90 ? "Optimal" : "Degraded"}
              changeType={operationalPercent >= 90 ? "increase" : "decrease"}
              subtitle="Fleet availability"
            />
            <StatCard
              title="Active Alerts"
              value={totalActiveAlerts}
              icon={AlertTriangle}
              color={Number(alerts.critical) > 0 ? "red" : totalActiveAlerts > 0 ? "yellow" : "green"}
              change={Number(alerts.critical) > 0 ? `${alerts.critical} critical` : null}
              changeType="decrease"
              subtitle={totalActiveAlerts > 0 ? "Attention required" : "All systems normal"}
            />
            <StatCard
              title="Maintenance Load"
              value={pendingMaintenanceTotal}
              icon={Wrench}
              color={Number(maintenance.overdue) > 0 ? "red" : "purple"}
              change={Number(maintenance.overdue) > 0 ? `${maintenance.overdue} overdue` : "On track"}
              changeType={Number(maintenance.overdue) > 0 ? "decrease" : "increase"}
              subtitle={`${maintenance.inProgress || 0} active, ${maintenance.upcoming || 0} queued`}
            />
            <StatCard
              title="Predictive Risks"
              value={pendingInsightCount}
              icon={TrendingUp}
              color={pendingInsightCount > 0 ? "yellow" : "blue"}
              change={Number(predictiveInsights.critical) > 0 ? `${predictiveInsights.critical} high risk` : null}
              changeType="decrease"
              subtitle={pendingInsightCount > 0 ? "Early anomaly alerts" : "Zero failure risk"}
            />
            <StatCard
              title="Resolution SLA"
              value="42m"
              icon={Clock}
              color="green"
              change="96% SLA"
              changeType="increase"
              subtitle="Avg turnaround time"
            />
          </div>

          {/* Row 2: Equipment Health Distribution & Maintenance Activity Trend */}
          <div
            className={clsx(
              "grid gap-4 sm:gap-6 transition-all duration-300",
              sidebarOpen
                ? "grid-cols-1 lg:grid-cols-3"
                : "grid-cols-1 lg:grid-cols-12"
            )}
          >
            {/* Machine Health Distribution Card */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className={clsx(
                "bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col justify-between transition-all duration-300",
                sidebarOpen ? "lg:col-span-1" : "lg:col-span-4"
              )}
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-slate-900">
                      Fleet Health Breakdown
                    </h2>
                    <p className="text-xs text-slate-500">
                      Current operating readiness
                    </p>
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    {operationalPercent}% Healthy
                  </span>
                </div>

                {/* Proportion Bar */}
                <div className="w-full h-3 rounded-full bg-slate-100 overflow-hidden flex mb-5">
                  <div
                    style={{ width: `${operationalPercent}%` }}
                    className="bg-emerald-500 transition-all duration-500"
                    title={`Operational: ${operationalCount}`}
                  />
                  <div
                    style={{ width: `${inMaintenancePercent}%` }}
                    className="bg-amber-500 transition-all duration-500"
                    title={`In Maintenance: ${inMaintenanceCount}`}
                  />
                  <div
                    style={{ width: `${offlinePercent}%` }}
                    className="bg-rose-500 transition-all duration-500"
                    title={`Offline / Error: ${offlineErrorCount}`}
                  />
                </div>

                {/* Distribution Rows */}
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between p-3 bg-emerald-50/70 border border-emerald-100 rounded-xl transition-colors hover:bg-emerald-50">
                    <span className="text-emerald-900 font-medium text-xs sm:text-sm flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-sm" />
                      Operational
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-emerald-700">
                        {operationalPercent}%
                      </span>
                      <span className="text-base sm:text-lg font-bold text-emerald-950">
                        {operationalCount}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-amber-50/70 border border-amber-100 rounded-xl transition-colors hover:bg-amber-50">
                    <span className="text-amber-900 font-medium text-xs sm:text-sm flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shadow-sm" />
                      In Maintenance
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-amber-700">
                        {inMaintenancePercent}%
                      </span>
                      <span className="text-base sm:text-lg font-bold text-amber-950">
                        {inMaintenanceCount}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-rose-50/70 border border-rose-100 rounded-xl transition-colors hover:bg-rose-50">
                    <span className="text-rose-900 font-medium text-xs sm:text-sm flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shadow-sm" />
                      Error / Offline
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-rose-700">
                        {offlinePercent}%
                      </span>
                      <span className="text-base sm:text-lg font-bold text-rose-950">
                        {offlineErrorCount}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <Link
                to="/machines"
                className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs sm:text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                <span>Explore Machine Fleet</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>

            {/* Maintenance Activity Trend Chart */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={clsx(
                "bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col justify-between transition-all duration-300",
                sidebarOpen ? "lg:col-span-2" : "lg:col-span-8"
              )}
            >
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-slate-900">
                      Maintenance & Operational Activity
                    </h2>
                    <p className="text-xs text-slate-500">
                      Work orders completed over the past 7 days
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 px-2.5 py-1 rounded-lg">
                      <Clock className="w-3.5 h-3.5 text-slate-500" /> Past 7 Days
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg">
                      <TrendingUp className="w-3.5 h-3.5 text-blue-600" /> High Uptime
                    </span>
                  </div>
                </div>

                <div className="h-56 sm:h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={defaultTrendData}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorTasks" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#f1f5f9"
                      />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#64748b", fontSize: 12 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#64748b", fontSize: 12 }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "12px",
                          border: "1px solid #e2e8f0",
                          backgroundColor: "rgba(255, 255, 255, 0.96)",
                          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                          fontSize: "12px",
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="tasks"
                        name="Work Orders"
                        stroke="#2563eb"
                        strokeWidth={2.5}
                        fillOpacity={1}
                        fill="url(#colorTasks)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="mt-2 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>Average turnaround time: <strong>42 mins</strong></span>
                <Link
                  to="/maintenance"
                  className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-0.5"
                >
                  <span>View Maintenance Hub</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Row 3: Active Alerts, Recent Activity & Quick Operations */}
          <div
            className={clsx(
              "grid gap-4 sm:gap-6 transition-all duration-300",
              sidebarOpen
                ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
                : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            )}
          >
            {/* Active Alerts Panel */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h2 className="text-base sm:text-lg font-bold text-slate-900">
                      Active Alerts
                    </h2>
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                    {alertsData.length} Recent
                  </span>
                </div>

                {alertsData && alertsData.length > 0 ? (
                  <div className="space-y-2.5">
                    {alertsData.slice(0, 3).map((alert) => (
                      <AlertItem key={alert.id || alert.title} alert={alert} />
                    ))}
                  </div>
                ) : (
                  <div className="p-6 rounded-xl border border-dashed border-slate-200 text-center flex flex-col items-center justify-center text-slate-500">
                    <CheckCircle className="w-8 h-8 text-emerald-500 mb-2" />
                    <p className="text-sm font-semibold text-slate-800">
                      All Systems Operating Normally
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      No active equipment anomalies detected
                    </p>
                  </div>
                )}
              </div>

              <Link
                to="/errors"
                className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs sm:text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                <span>View All Incident Reports</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>

            {/* Recent Maintenance Activities */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h2 className="text-base sm:text-lg font-bold text-slate-900">
                      Recent Work Logs
                    </h2>
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    Shop Floor Logs
                  </span>
                </div>

                {recentActivity && recentActivity.length > 0 ? (
                  <div className="space-y-2.5">
                    {recentActivity.slice(0, 3).map((activity) => (
                      <div
                        key={activity.id}
                        className="p-3 rounded-xl border border-slate-100 bg-slate-50/60 hover:bg-slate-50 transition-colors flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-xs sm:text-sm font-semibold text-slate-900 truncate">
                            {activity.title || "Routine Inspection"}
                          </p>
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {activity.machine_name || "Machine Unit"}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span
                            className={clsx(
                              "inline-block px-2 py-0.5 rounded-md text-[11px] font-semibold capitalize",
                              activity.status === "completed"
                                ? "bg-emerald-100 text-emerald-700"
                                : activity.status === "in_progress"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-blue-100 text-blue-700"
                            )}
                          >
                            {activity.status?.replace("_", " ") || "Pending"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 rounded-xl border border-dashed border-slate-200 text-center flex flex-col items-center justify-center text-slate-500">
                    <Calendar className="w-8 h-8 text-slate-400 mb-2" />
                    <p className="text-sm font-semibold text-slate-800">
                      No Recent Activity
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Completed maintenance tasks will appear here
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>Synchronized with maintenance telemetry</span>
                <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live Feed
                </span>
              </div>
            </motion.div>

            {/* Quick Operations & AI Shortcuts */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className={clsx(
                "bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col justify-between transition-all duration-300",
                sidebarOpen ? "md:col-span-2 xl:col-span-1" : "md:col-span-2 lg:col-span-1"
              )}
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h2 className="text-base sm:text-lg font-bold text-slate-900">
                      Quick Actions
                    </h2>
                  </div>
                  {pendingInsightCount > 0 && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                      {pendingInsightCount} AI Insights
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <Link
                    to="/maintenance/schedule"
                    className="p-3 rounded-xl border border-slate-200/90 bg-slate-50/50 hover:bg-blue-50 hover:border-blue-200 transition-all group flex flex-col justify-between"
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Calendar className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                        Schedule Task
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Plan maintenance
                      </p>
                    </div>
                  </Link>

                  <Link
                    to="/errors/report"
                    className="p-3 rounded-xl border border-slate-200/90 bg-slate-50/50 hover:bg-rose-50 hover:border-rose-200 transition-all group flex flex-col justify-between"
                  >
                    <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <ShieldAlert className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900 group-hover:text-rose-600 transition-colors">
                        Log Machine Issue
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Report equipment error
                      </p>
                    </div>
                  </Link>

                  <Link
                    to="/sops"
                    className="p-3 rounded-xl border border-slate-200/90 bg-slate-50/50 hover:bg-emerald-50 hover:border-emerald-200 transition-all group flex flex-col justify-between"
                  >
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">
                        Browse SOPs
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Standard procedures
                      </p>
                    </div>
                  </Link>

                  <Link
                    to="/predictive"
                    className="p-3 rounded-xl border border-slate-200/90 bg-slate-50/50 hover:bg-amber-50 hover:border-amber-200 transition-all group flex flex-col justify-between"
                  >
                    <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900 group-hover:text-amber-600 transition-colors">
                        Predictive Insights
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Early health risks
                      </p>
                    </div>
                  </Link>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                <span className="text-slate-500">Documentation & manuals</span>
                <Link
                  to="/knowledge"
                  className="flex items-center justify-between text-xs sm:text-sm font-semibold text-blue-600 hover:text-blue-700"
                >
                  <span>Knowledge Base</span>
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
